import { Box, Icon, visuallyHidden } from "@outschool/backpack";
import { faBars, faComment, faSearch } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  StandardHeaderLeft,
  useMobileSearchBar
} from "@outschool/ui-components-website";
import {
  ConditionalTrackingPageSection,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { Screen, useOnScreen } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router-dom";

import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import { useStickyHeader } from "../../hooks/useStickyHeader";
import { UserMode, useUserMode, useUserModeRootPath } from "../../lib/UserMode";
import Link from "../Link";
import { DesktopHeaderNavigation } from "./DesktopHeaderNav";
import { EducatorModeToolbar } from "./EducatorModeToolbar";
import { EsaMembershipNavItem } from "./EsaMembershipNavItem";
import GlobalSearchBar, { MobileSearchBar } from "./GlobalSearchBar";
import { HeaderMenu, HeaderMenuButtonProps } from "./HeaderMenu";
import { JoinSubscriptionNavItem } from "./JoinSubscriptionNavItem";
import { useShouldShowMobileFooterTabBar } from "./MobileFooterTabBar";
import { SubscriptionCreditNavItem } from "./SubscriptionCreditNavItem";

interface Props {
  trackingUniqueId?: string;
  showCategoriesNavbar?: boolean;
  showSearchBox?: boolean;
  hideHeaderRight?: boolean;
  sx?: any;
}

export const HEADER_WIDTH = 1255;

export default function Header({
  trackingUniqueId,
  showCategoriesNavbar = false,
  showSearchBox = true,
  hideHeaderRight = false,
  sx
}: Props) {
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;

  return (
    <ConditionalTrackingPageSection
      name="header"
      uniqueId={trackingUniqueId ? trackingUniqueId : "header"}
    >
      {setImpressionNode => (
        <Box ref={setImpressionNode}>
          <StandardHeader
            hideFindClasses={isEducatorMode}
            showCategoriesNavbar={showCategoriesNavbar}
            showSearchBox={showSearchBox}
            rightChildren={hideHeaderRight ? null : undefined}
            sx={sx}
          />
        </Box>
      )}
    </ConditionalTrackingPageSection>
  );
}

function ListingsPageLink({ t }: { t: TFunction }) {
  const { pathname } = useLocation();
  const isSearchPage = pathname === searchPath();
  return (
    <Link to={searchPath()} trackingName="nav_find_classes">
      <Box
        sx={{
          backgroundColor: isSearchPage ? "primary.200" : "common.white",
          color: !isSearchPage ? "grey.800" : "text",
          borderRadius: "100%",
          lineHeight: 0
        }}
      >
        <Icon
          icon={faSearch}
          sx={{
            fontSize: "1.333em",
            margin: "12px"
          }}
        />
      </Box>
      <span style={visuallyHidden}>{t`Search`}</span>
    </Link>
  );
}

export function MobileHeaderMenuButton() {
  const { t } = useTranslation("client\\components\\nav\\Header");
  const [numCloseCount, setNumCloseCount] = React.useState(0);

  return (
    <HeaderMenu
      onClose={() => {
        setNumCloseCount(prev => prev + 1);
      }}
      opensDrawer
    >
      {({ isOpen: _isOpen, buttonRef, ...props }: HeaderMenuButtonProps) => (
        <TrackedButton
          variant="link"
          trackingName="mobile_header_menu_open_button"
          trackingUniqueId={`mobile_header_menu_open_button-${numCloseCount}`}
          trackingSharedProperties={{
            numCloseCount
          }}
          aria-label={t("Menu")}
          ref={buttonRef}
          {...props}
        >
          <Icon
            icon={faBars}
            sx={{
              fontSize: "1.333em",
              color: "gray"
            }}
          />
        </TrackedButton>
      )}
    </HeaderMenu>
  );
}

function MobileMessagesLink({ t }: { t: TFunction }) {
  const { isLeader, currentUser } = useSession();
  const unreadConversationsCount = User.unreadConversationsCount(currentUser);

  const { trackTouch } = useImpressionTracking({
    trackingLabel: "mobile_nav_header_messages",
    uniqueId: "mobile_nav_header_messages",
    trackingEventName: "mobile_nav_header_messages_event"
  });

  return (
    <Box
      as={Link}
      onClick={() => trackTouch()}
      sx={{
        position: "relative",
        display: "flex",
        borderRadius: "100%",
        color: "grey.950",
        ['&[aria-current="page"]']: {
          backgroundColor: "primary.200",
          color: "primary.main"
        }
      }}
      to={Routes.conversationsPath(
        isLeader ? UserMode.Educator : UserMode.Parent
      )}
    >
      {unreadConversationsCount > 0 && (
        <Box
          role="status"
          aria-label={t("{{unreadCount}} unread message", {
            unreadCount: unreadConversationsCount
          })}
          sx={(theme: $TSFixMe) => ({
            position: "absolute",
            top: "0.5rem",
            right: "0.3rem",
            border: "1px solid white",
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white,
            borderRadius: "50%",
            fontSize: "1.2rem",
            minWidth: "1.7rem",
            lineHeight: "1.5rem",
            textAlign: "center"
          })}
        >
          {unreadConversationsCount}
        </Box>
      )}

      <Icon
        aria-label={t("Conversations")}
        icon={faComment}
        sx={{
          fontSize: "1.333em",
          margin: "12px",
          color: "inherit"
        }}
      />
    </Box>
  );
}

function StandardHeaderRight({ hideFindClasses, t }: $TSFixMe) {
  const isMobile = Screen.useIsMobile();
  const { isLoggedIn, currentUser } = useSession();
  const mobileFooterIsShowing = useShouldShowMobileFooterTabBar();
  const hasActiveSubscription = currentUser?.hasSubscription;
  const hasEsaMembership = currentUser?.hasEsaMembership;

  return (
    <>
      {isMobile ? (
        <>
          {hasEsaMembership && <EsaMembershipNavItem />}
          {!hasEsaMembership && hasActiveSubscription && (
            <SubscriptionCreditNavItem />
          )}
          {!hasEsaMembership && !hasActiveSubscription && (
            <JoinSubscriptionNavItem />
          )}
          <Box
            flex
            sx={{
              gap: "0.25em",
              paddingY: "0.5em"
            }}
          >
            {isLoggedIn && <MobileMessagesLink t={t} />}
            {!mobileFooterIsShowing && !hideFindClasses && (
              <ListingsPageLink t={t} />
            )}
            <MobileHeaderMenuButton />
          </Box>
        </>
      ) : (
        <DesktopHeaderNavigation />
      )}
    </>
  );
}

const HeaderToolbar = ({ showSearchBox = true }) => {
  const isMobile = Screen.useIsMobile();
  const isEducatorMode = useUserMode() === UserMode.Educator;
  const { setIsHeaderMounted } = useMobileSearchBar();

  React.useEffect(() => {
    const t = setTimeout(() => setIsHeaderMounted(true), 1000);
    return () => {
      clearTimeout(t);
      setIsHeaderMounted(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { pathname } = useLocation();

  const classDetailPathPattern = /^\/classes\/[\w-]+$/;
  const isClassDetailPagePath = classDetailPathPattern.test(pathname);

  if (isMobile && showSearchBox && !isEducatorMode && !isClassDetailPagePath) {
    return <MobileSearchBar />;
  }

  if (!isMobile) {
    if (isEducatorMode) {
      return <EducatorModeToolbar />;
    }
    return <GlobalSearchBar showSearchBox={showSearchBox} />;
  }
  return null;
};

interface StandardHeaderProps {
  sx?: any;
  hideFindClasses?: boolean;
  leftChildren?: any;
  rightChildren?: any;
  showCategoriesNavbar?: boolean;
  showSearchBox?: boolean;
}
export const StandardHeader = ({
  sx,
  hideFindClasses,
  leftChildren,
  rightChildren,
  showCategoriesNavbar,
  showSearchBox = true
}: StandardHeaderProps) => {
  const ref = React.useRef<HTMLElement>(null);
  const { t } = useTranslation("client\\components\\nav\\Header"); //StickyMobileHeader imports this as well

  const { setIsHeaderOnScreen } = useMobileSearchBar();
  const isOnScreen = useOnScreen(ref.current);
  const rootPath = useUserModeRootPath();
  const { scrolled } = useStickyHeader();

  React.useEffect(() => {
    setIsHeaderOnScreen(isOnScreen);
  }, [setIsHeaderOnScreen, isOnScreen]);

  if (leftChildren === undefined) {
    // if null, omit left header
    leftChildren = (
      <StandardHeaderLeft
        rootPath={rootPath}
        showCategoriesNavbar={showCategoriesNavbar}
      />
    );
  }
  if (rightChildren === undefined) {
    // if null, omit right header
    rightChildren = (
      <StandardHeaderRight hideFindClasses={hideFindClasses} t={t} />
    );
  }
  return (
    <Box
      ref={ref}
      sx={{
        zIndex: 201,
        position: "relative",
        backgroundColor: "common.white",
        transition: "box-shadow 0.2s ease-in-out",
        ...(scrolled ? { boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)" } : {}),
        ...sx
      }}
    >
      <Box
        flex
        sx={(theme: $TSFixMe) => ({
          gap: "1em",
          paddingY: 5,
          maxWidth: HEADER_WIDTH,
          justifyContent: "center",
          alignItems: "center",
          marginLeft: "auto",
          marginRight: "auto",
          [theme.breakpoints.down("md")]: {
            paddingY: 0,
            justifyContent: "space-between"
          },
          [theme.breakpoints.down("sm")]: {
            gap: "0.5em"
          },
          // Needs to be in sync with HEADER_WIDTH
          // probably doesn't 100% work with backpack's text size scaling?
          // TODO figure out the "right" way to do this
          "@media (max-width: 1255px)": {
            mx: 24
          }
        })}
      >
        {leftChildren}
        <HeaderToolbar showSearchBox={showSearchBox} />
        {rightChildren}
      </Box>
    </Box>
  );
};
